import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Eye, Truck, Download } from 'lucide-react';
import { inventoryApi, shipmentsApi, customersApi, staffApi } from '../../lib/api';
import PageHeader from '../../components/PageHeader';
import DataTable from '../../components/DataTable';
import Modal from '../../components/Modal';

export default function InventoryFrozen() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [isShipmentModalOpen, setIsShipmentModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedInventory, setSelectedInventory] = useState<any>(null);
  const [shipmentForm, setShipmentForm] = useState({
    shipmentDate: new Date().toISOString().split('T')[0],
    customerId: '',
    staffId: '',
    quantity: '',
    weight: '',
    unitPrice: '',
    arrivalDate: '',
    isNameChange: false,
    remarks: '',
  });

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['inventory', 'frozen', search],
    queryFn: () => inventoryApi.getFrozen({ search, hasStock: 'true' }),
  });

  const { data: customersData } = useQuery({
    queryKey: ['customers'],
    queryFn: () => customersApi.getAll({ active: 'true' }),
  });

  const { data: staffData } = useQuery({
    queryKey: ['staff'],
    queryFn: () => staffApi.getAll({ active: 'true' }),
  });

  const shipmentMutation = useMutation({
    mutationFn: shipmentsApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      queryClient.invalidateQueries({ queryKey: ['shipments'] });
      toast.success('出庫を登録しました');
      setIsShipmentModalOpen(false);
      resetShipmentForm();
    },
    onError: () => toast.error('出庫登録に失敗しました'),
  });

  const resetShipmentForm = () => {
    setShipmentForm({
      shipmentDate: new Date().toISOString().split('T')[0],
      customerId: '',
      staffId: '',
      quantity: '',
      weight: '',
      unitPrice: '',
      arrivalDate: '',
      isNameChange: false,
      remarks: '',
    });
    setSelectedInventory(null);
  };

  const handleShipment = (row: any) => {
    setSelectedInventory(row);
    setShipmentForm({
      ...shipmentForm,
      quantity: row.inventory?.currentQuantity?.toString() || '',
      weight: row.inventory?.currentWeight?.toString() || '',
      unitPrice: row.purchase?.unitPrice?.toString() || '',
    });
    setIsShipmentModalOpen(true);
  };

  const handleDetail = (row: any) => {
    setSelectedInventory(row);
    setIsDetailModalOpen(true);
  };

  const handleShipmentSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedInventory) return;

    shipmentMutation.mutate({
      shipmentDate: shipmentForm.shipmentDate,
      purchaseId: selectedInventory.purchase.id,
      customerId: shipmentForm.customerId,
      staffId: shipmentForm.staffId || undefined,
      quantity: parseInt(shipmentForm.quantity),
      weight: shipmentForm.weight,
      unitPrice: shipmentForm.unitPrice,
      arrivalDate: shipmentForm.arrivalDate || undefined,
      isNameChange: shipmentForm.isNameChange,
      remarks: shipmentForm.remarks,
    });
  };

  const handleExportCSV = () => {
    const items = data?.data?.data || [];
    if (items.length === 0) {
      toast.error('エクスポートするデータがありません');
      return;
    }

    const headers = ['社内LOT', '仕入日', '仕入先', '商品名', 'ブランド', '冷蔵庫', '単価', '残数', '残重量', '在庫金額', '賞味期限'];
    const rows = items.map((item: any) => [
      item.purchase?.internalLot || '',
      item.purchase?.purchaseDate || '',
      item.supplier?.name || '',
      item.purchase?.productName || '',
      item.brand?.name || item.purchase?.brandName || '',
      item.coldStorage?.name || '',
      item.purchase?.unitPrice || '',
      item.inventory?.currentQuantity || '',
      item.inventory?.currentWeight || '',
      item.inventory?.inventoryValue || '',
      item.purchase?.expiryDate || '',
    ]);

    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `冷凍在庫_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('CSVをエクスポートしました');
  };

  const formatNumber = (num: any) => {
    if (num === null || num === undefined) return '-';
    return Number(num).toLocaleString('ja-JP');
  };

  const summary = data?.data?.summary || { totalQuantity: 0, totalWeight: 0, totalValue: 0 };

  const columns = [
    { 
      key: 'purchase.internalLot', 
      label: '社内LOT', 
      width: '100px',
      render: (row: any) => row.purchase?.internalLot || '-',
    },
    { 
      key: 'purchase.purchaseDate', 
      label: '仕入日', 
      width: '100px',
      render: (row: any) => row.purchase?.purchaseDate || '-',
    },
    { 
      key: 'supplier.name', 
      label: '仕入先',
      render: (row: any) => row.supplier?.name || '-',
    },
    { 
      key: 'purchase.productName', 
      label: '商品名',
      render: (row: any) => row.purchase?.productName || '-',
    },
    { 
      key: 'brand.name', 
      label: 'ブランド',
      render: (row: any) => row.brand?.name || row.purchase?.brandName || '-',
    },
    { 
      key: 'coldStorage.name', 
      label: '冷蔵庫',
      render: (row: any) => row.coldStorage?.name || '-',
    },
    { 
      key: 'purchase.unitPrice', 
      label: '単価',
      width: '80px',
      render: (row: any) => formatNumber(row.purchase?.unitPrice),
    },
    { 
      key: 'inventory.currentQuantity', 
      label: '残数',
      width: '70px',
      render: (row: any) => formatNumber(row.inventory?.currentQuantity),
    },
    { 
      key: 'inventory.currentWeight', 
      label: '残重量',
      width: '90px',
      render: (row: any) => formatNumber(row.inventory?.currentWeight),
    },
    { 
      key: 'inventory.inventoryValue', 
      label: '在庫金額',
      width: '110px',
      render: (row: any) => `¥${formatNumber(row.inventory?.inventoryValue)}`,
    },
    { 
      key: 'purchase.expiryDate', 
      label: '賞味期限',
      width: '100px',
      render: (row: any) => row.purchase?.expiryDate || '-',
    },
    {
      key: 'actions',
      label: '操作',
      width: '80px',
      render: (row: any) => (
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); handleDetail(row); }}
            className="p-1.5 rounded-lg transition-colors hover:bg-gray-100"
            style={{ color: 'var(--accent-color)' }}
            title="詳細"
          >
            <Eye size={16} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); handleShipment(row); }}
            className="p-1.5 rounded-lg transition-colors hover:bg-gray-100"
            style={{ color: 'var(--success-color)' }}
            title="出庫"
          >
            <Truck size={16} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="animate-fadeIn">
      <PageHeader
        title="冷凍在庫一覧"
        breadcrumbs={[{ label: '在庫管理' }]}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="商品名で検索..."
        onApply={() => refetch()}
        actions={
          <button className="btn btn-outline btn-sm" onClick={handleExportCSV}>
            <Download size={14} />
          </button>
        }
      />

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="card flex items-center gap-4">
          <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(139, 92, 246, 0.15)' }}>
            <span className="text-xl font-bold" style={{ color: 'var(--frozen-color)' }}>📦</span>
          </div>
          <div>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>総在庫数</p>
            <p className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>
              {formatNumber(summary.totalQuantity)} ケース
            </p>
          </div>
        </div>
        <div className="card flex items-center gap-4">
          <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(139, 92, 246, 0.15)' }}>
            <span className="text-xl font-bold" style={{ color: 'var(--frozen-color)' }}>⚖️</span>
          </div>
          <div>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>総重量</p>
            <p className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>
              {formatNumber(summary.totalWeight)} kg
            </p>
          </div>
        </div>
        <div className="card flex items-center gap-4">
          <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(139, 92, 246, 0.15)' }}>
            <span className="text-xl font-bold" style={{ color: 'var(--frozen-color)' }}>💰</span>
          </div>
          <div>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>在庫金額</p>
            <p className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>
              ¥{formatNumber(summary.totalValue)}
            </p>
          </div>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={data?.data?.data || []}
        loading={isLoading}
        emptyMessage="冷凍在庫がありません"
        defaultSortKey="purchase.expiryDate"
        defaultSortOrder="asc"
      />

      {/* Shipment Modal */}
      <Modal
        isOpen={isShipmentModalOpen}
        onClose={() => { setIsShipmentModalOpen(false); resetShipmentForm(); }}
        title="出庫登録"
        size="lg"
      >
        {selectedInventory && (
          <form onSubmit={handleShipmentSubmit} className="space-y-4">
            <div className="p-4 rounded-lg mb-4" style={{ backgroundColor: 'var(--light-gray-tertiary)' }}>
              <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
                {selectedInventory.purchase?.productName}
              </p>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                LOT: {selectedInventory.purchase?.internalLot} | 
                残数: {selectedInventory.inventory?.currentQuantity}ケース | 
                残重量: {formatNumber(selectedInventory.inventory?.currentWeight)}kg
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">出庫日 <span className="text-red-500">*</span></label>
                <input
                  type="date"
                  className="input-field"
                  value={shipmentForm.shipmentDate}
                  onChange={(e) => setShipmentForm({ ...shipmentForm, shipmentDate: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="label">売り先 <span className="text-red-500">*</span></label>
                <select
                  className="input-field"
                  value={shipmentForm.customerId}
                  onChange={(e) => setShipmentForm({ ...shipmentForm, customerId: e.target.value })}
                  required
                >
                  <option value="">選択してください</option>
                  {customersData?.data?.data?.map((customer: any) => (
                    <option key={customer.id} value={customer.id}>{customer.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">担当者</label>
                <select
                  className="input-field"
                  value={shipmentForm.staffId}
                  onChange={(e) => setShipmentForm({ ...shipmentForm, staffId: e.target.value })}
                >
                  <option value="">選択してください</option>
                  {staffData?.data?.data?.map((s: any) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">着日</label>
                <input
                  type="date"
                  className="input-field"
                  value={shipmentForm.arrivalDate}
                  onChange={(e) => setShipmentForm({ ...shipmentForm, arrivalDate: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="label">数量(ケース) <span className="text-red-500">*</span></label>
                <input
                  type="number"
                  className="input-field"
                  value={shipmentForm.quantity}
                  onChange={(e) => setShipmentForm({ ...shipmentForm, quantity: e.target.value })}
                  max={selectedInventory.inventory?.currentQuantity}
                  required
                />
              </div>
              <div>
                <label className="label">重量(kg) <span className="text-red-500">*</span></label>
                <input
                  type="number"
                  step="0.01"
                  className="input-field"
                  value={shipmentForm.weight}
                  onChange={(e) => setShipmentForm({ ...shipmentForm, weight: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="label">売単価(円/kg) <span className="text-red-500">*</span></label>
                <input
                  type="number"
                  step="0.01"
                  className="input-field"
                  value={shipmentForm.unitPrice}
                  onChange={(e) => setShipmentForm({ ...shipmentForm, unitPrice: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="isNameChange"
                checked={shipmentForm.isNameChange}
                onChange={(e) => setShipmentForm({ ...shipmentForm, isNameChange: e.target.checked })}
              />
              <label htmlFor="isNameChange" className="text-sm" style={{ color: 'var(--text-primary)' }}>
                名変（名義変更）
              </label>
            </div>

            <div>
              <label className="label">備考</label>
              <textarea
                className="input-field"
                rows={2}
                value={shipmentForm.remarks}
                onChange={(e) => setShipmentForm({ ...shipmentForm, remarks: e.target.value })}
              />
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t" style={{ borderColor: 'var(--border-color)' }}>
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => { setIsShipmentModalOpen(false); resetShipmentForm(); }}
              >
                キャンセル
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={shipmentMutation.isPending}
              >
                出庫登録
              </button>
            </div>
          </form>
        )}
      </Modal>

      {/* Detail Modal */}
      <Modal
        isOpen={isDetailModalOpen}
        onClose={() => { setIsDetailModalOpen(false); setSelectedInventory(null); }}
        title="在庫詳細"
        size="lg"
      >
        {selectedInventory && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>社内LOT</p>
                <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
                  {selectedInventory.purchase?.internalLot}
                </p>
              </div>
              <div>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>仕入日</p>
                <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
                  {selectedInventory.purchase?.purchaseDate}
                </p>
              </div>
              <div>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>仕入先</p>
                <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
                  {selectedInventory.supplier?.name}
                </p>
              </div>
              <div>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>商品名</p>
                <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
                  {selectedInventory.purchase?.productName}
                </p>
              </div>
              <div>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>ブランド</p>
                <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
                  {selectedInventory.brand?.name || selectedInventory.purchase?.brandName || '-'}
                </p>
              </div>
              <div>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>冷蔵庫</p>
                <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
                  {selectedInventory.coldStorage?.name}
                </p>
              </div>
              <div>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>冷蔵庫ロット</p>
                <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
                  {selectedInventory.purchase?.coldStorageLot || '-'}
                </p>
              </div>
              <div>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>仕入単価</p>
                <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
                  ¥{formatNumber(selectedInventory.purchase?.unitPrice)}/kg
                </p>
              </div>
            </div>

            <div className="border-t pt-4" style={{ borderColor: 'var(--border-color)' }}>
              <h4 className="font-medium mb-3" style={{ color: 'var(--text-primary)' }}>在庫状況</h4>
              <div className="grid grid-cols-4 gap-4">
                <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--light-gray-tertiary)' }}>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>入庫数</p>
                  <p className="font-bold text-lg" style={{ color: 'var(--text-primary)' }}>
                    {formatNumber(selectedInventory.purchase?.quantity)} ケース
                  </p>
                </div>
                <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--light-gray-tertiary)' }}>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>入庫重量</p>
                  <p className="font-bold text-lg" style={{ color: 'var(--text-primary)' }}>
                    {formatNumber(selectedInventory.purchase?.weight)} kg
                  </p>
                </div>
                <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--light-gray-tertiary)' }}>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>残数</p>
                  <p className="font-bold text-lg" style={{ color: 'var(--frozen-color)' }}>
                    {formatNumber(selectedInventory.inventory?.currentQuantity)} ケース
                  </p>
                </div>
                <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--light-gray-tertiary)' }}>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>残重量</p>
                  <p className="font-bold text-lg" style={{ color: 'var(--frozen-color)' }}>
                    {formatNumber(selectedInventory.inventory?.currentWeight)} kg
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>在庫金額</p>
                <p className="font-bold text-xl" style={{ color: 'var(--success-color)' }}>
                  ¥{formatNumber(selectedInventory.inventory?.inventoryValue)}
                </p>
              </div>
              <div>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>賞味期限</p>
                <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
                  {selectedInventory.purchase?.expiryDate || '-'}
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t" style={{ borderColor: 'var(--border-color)' }}>
              <button
                className="btn btn-outline"
                onClick={() => { setIsDetailModalOpen(false); setSelectedInventory(null); }}
              >
                閉じる
              </button>
              <button
                className="btn btn-primary"
                onClick={() => {
                  setIsDetailModalOpen(false);
                  handleShipment(selectedInventory);
                }}
              >
                <Truck size={16} />
                出庫登録
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
